const History = require("history");
const Constants = require("../../shared/Constants");
const {parseURL} = require("../../shared/URLParser");
const {requiresFullDocumentNavigation} = require("../../shared/ElevRoutes");

// Internal helper
const startHistoryListen = function(router) {
	if (global.PLAY_PROBLEM_DATA) {
		return;
	}
	router.history.listen(function(location) {
		if (!router.changingURLSilently) {
			const pageState = parseURL(location.pathname, router.extraPageData);
			const options = {
				fromBrowser: !router.inSetURLCall,
			};
			router.dispatchPage(pageState, options);
		}
	});
};

// Internal helper
const runURLHooks = function(router, page, href, options) {
	if (!options.silent) {
		for (let i = 0; i < router.urlHookList.length; i += 1) {
			const hook = router.urlHookList[i];
			const ret = hook(router.vault, page, href, options);
			if (ret === false) {
				return false;
			}
		}
	}
	return true;
};

// Constructor
/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const Router = function(vault, dispatchPage) {
	this.vault = vault;
	this.dispatchPage = dispatchPage;
	this.history = process.env.IS_NODE
		? History.createMemoryHistory()
		: History.createBrowserHistory();
	this.inSetURLCall = false;
	this.changingURLSilently = false;
	this.extraPageData = null;
	this.lastHref = null;
	this.urlHookList = [];
	startHistoryListen(this);
};

// Available options
// - replace: replace the URL in the state stack
// - silent: don't have the router handle the URL
// - pageData: extra data to include in the page object
Router.prototype.setURL = function(href, options = {}) {
	if (global.PLAY_PROBLEM_DATA || !href) {
		return;
	}
	const page = parseURL(href, options.pageData);

	// Run hooks and deny the page change if any return false.
	const allowedByHooks = runURLHooks(this, page, href, options);
	if (!allowedByHooks) {
		return;
	}

	// Preserve query strings unless we're changing the URL for real. This is
	// very important to preserve things like the gclid from google analytics.
	// See T15496.
	if (options.replace && global.window) {
		const qs = window.location.search;
		if (qs && !/\?/.test(href)) {
			href += qs;
		}
	}

	if (requiresFullDocumentNavigation(page)) {
		const {getFeatureFlag} = require("./util/FeatureFlags");
		if (
			page.name === "office.contact" &&
			getFeatureFlag("ENABLE_NEW_CONTACT_US")
		) {
			window.location = Constants.NEW_CONTACT_US_EXTERNAL_URL;
			return;
		}

		// Special case: office.main has both an elev route (for logged-out users)
		// and a React component (for logged-in users). Only redirect if we know
		// the user is NOT logged in (load complete and not authenticated/initialized).
		// Don't redirect while user is still loading to avoid infinite reload loops.
		// Uninitialized Platform users should see the marketing homepage.
		const vaultState = this.vault.getState();
		const user = vaultState && vaultState.user;
		const OpsUser = require("./vault/OpsUser");
		const isOfficeMain = page.name === "office.main";
		const isUserInitialized = user && OpsUser.isInitialized(user);
		const shouldRedirect = !(
			isOfficeMain &&
			(!user ||
				user.load !== "done" ||
				(user.authenticated && isUserInitialized))
		);

		if (shouldRedirect) {
			window.location = href;
			return;
		}
	}

	const methodName = options.replace ? "replace" : "push";

	// Sentry picks up noop URL replaces. So we suppress those.
	// TODO: figure out if we can remove this
	if (methodName === "push" || this.lastHref !== href) {
		// The history method call here indirectly triggers the listener in
		// startHistoryListen. We need to temporarily set some state variables for
		// that listener...
		this.inSetURLCall = true;
		this.extraPageData = options.pageData;
		this.changingURLSilently = !!options.silent;

		this.history[methodName](href);
		this.lastHref = href;

		// ... then unset the temporary state.
		this.inSetURLCall = false;
		this.changingURLSilently = false;
		this.extraPageData = null;
	}
};

// Pass a function to call just before a URL change. The function is passed
// (page, href, options), and if it explicitly returns false, no further hooks
// or the URL change will run. Hooks are run in order.
Router.prototype.addURLHook = function(hook) {
	typeof hook === "function" && this.urlHookList.push(hook);
};

module.exports = Router;
